import { useCallback, useEffect, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../firebase/config";
import {
  classroomMemberId,
  isValidClassroomCode,
  normalizeClassroomCode,
  type Classroom,
  type ClassroomMember,
} from "../lib/classroomEngine";

export function useStudentClassrooms(
  studentId: string | undefined,
  studentName: string
) {
  const [memberships, setMemberships] = useState<ClassroomMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let unsubSnapshot: (() => void) | undefined;

    const unsubAuth = onAuthStateChanged(auth, (firebaseUser) => {
      unsubSnapshot?.();
      unsubSnapshot = undefined;

      if (!firebaseUser?.uid) {
        setMemberships([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const q = query(
        collection(db, "bordona_classroom_members"),
        where("studentId", "==", firebaseUser.uid)
      );

      unsubSnapshot = onSnapshot(
        q,
        (snap) => {
          setMemberships(
            snap.docs.map((d) => ({ id: d.id, ...d.data() } as ClassroomMember))
          );
          setLoading(false);
        },
        (err) => {
          setError(err.message);
          setLoading(false);
        }
      );
    });

    return () => {
      unsubAuth();
      unsubSnapshot?.();
    };
  }, []);

  const joinClassroom = useCallback(
    async (rawCode: string): Promise<{ ok: boolean; message: string }> => {
      const authUid = auth.currentUser?.uid;
      if (!authUid) {
        return { ok: false, message: "Debes iniciar sesión en Edusico primero." };
      }
      if (studentId && studentId !== authUid) {
        return { ok: false, message: "La sesión no coincide con tu perfil. Recarga la página." };
      }

      const code = normalizeClassroomCode(rawCode);
      if (!isValidClassroomCode(code)) {
        return {
          ok: false,
          message: `El código de clase tiene ${8} caracteres (letras y números).`,
        };
      }

      setJoining(true);
      setError(null);

      try {
        const classSnap = await getDoc(doc(db, "bordona_classrooms", code));
        if (!classSnap.exists()) {
          setJoining(false);
          return { ok: false, message: "No existe ninguna clase con ese código." };
        }

        const classroom = classSnap.data() as Classroom;
        const memberId = classroomMemberId(code, authUid);
        const existing = await getDoc(doc(db, "bordona_classroom_members", memberId));
        if (existing.exists()) {
          setJoining(false);
          return { ok: true, message: `Ya estás en la clase «${classroom.title}».` };
        }

        const member: ClassroomMember = {
          id: memberId,
          classroomCode: code,
          classroomTitle: classroom.title,
          teacherId: classroom.teacherId,
          teacherName: classroom.teacherName,
          studentId: authUid,
          studentName: studentName.trim() || "Estudiante",
          joinedAt: Date.now(),
        };

        await setDoc(doc(db, "bordona_classroom_members", memberId), member);
        setJoining(false);
        return {
          ok: true,
          message: `Te uniste a «${classroom.title}» con ${classroom.teacherName || "tu profesor"}.`,
        };
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Error al unirse a la clase";
        setError(msg);
        setJoining(false);
        return { ok: false, message: msg };
      }
    },
    [studentId, studentName]
  );

  const isMemberOf = useCallback(
    (classroomCode: string | undefined) => {
      if (!classroomCode) return true;
      const code = normalizeClassroomCode(classroomCode);
      return memberships.some((m) => m.classroomCode === code);
    },
    [memberships]
  );

  return {
    memberships,
    loading,
    joining,
    error,
    joinClassroom,
    isMemberOf,
  };
}

export async function checkClassroomMembership(
  classroomId: string,
  studentId: string
): Promise<boolean> {
  const code = normalizeClassroomCode(classroomId);
  const memberId = classroomMemberId(code, studentId);
  const snap = await getDoc(doc(db, "bordona_classroom_members", memberId));
  return snap.exists();
}
